"use client"

// src/components/auth/org/CreateOrganization.tsx
import type React from "react"
import { useState, useEffect } from "react"
import { api } from "../../../services/Axios"
import NavBar from "../../utils/navbar/NavBar"
import MessageModal from "../../utils/avisos/MessageModal"
import Loader from "../../utils/loader/Loader"
import { FaArrowRight, FaArrowLeft, FaCheck, FaBuilding, FaMapMarkerAlt, FaIdCard, FaCheckCircle, FaPhone } from "react-icons/fa"

interface CreateOrganizationProps {
  userData: { nombre: string; role: string } | null
  onOrganizationCreated: () => void
  handleLogout: () => void
  syncAuthStatus: () => Promise<void>
}

const CreateOrganization: React.FC<CreateOrganizationProps> = ({
  userData,
  onOrganizationCreated,
  handleLogout,
  syncAuthStatus,
}) => {
  const [organizationName, setOrganizationName] = useState("")
  const [NIT, setNIT] = useState("")
  const [direccion, setDireccion] = useState("")
  const [ciudad, setCiudad] = useState("")
  const [razonSocial, setRazonSocial] = useState("")
  const [telefonoContacto, setTelefonoContacto] = useState("");
  const [responsableIVA, setResponsableIVA] = useState(false)
  const [notification, setNotification] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState("")
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [step, setStep] = useState(1)
  const [animate, setAnimate] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000)
  }, [])

  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setAnimate(false), 500)
      return () => clearTimeout(timer)
    }
  }, [animate])

  const handleNext = () => {
    setAnimate(true)
    setStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setAnimate(true)
    setStep((prevStep) => prevStep - 1)
  }

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.post("/api/auth/create-organization", {
        name: organizationName,
        razonSocial,
        NIT,
        direccion,
        ciudad,
        responsableIVA,
        telefonoContacto,
      })

      setNotification("¡Organización creada exitosamente!")
      localStorage.setItem("organizationCreated", "true")
      localStorage.setItem("showWelcomeMessage", "true")
      localStorage.setItem("showConfetti", "true")
      await syncAuthStatus()
      onOrganizationCreated()
    } catch (error: any) {
      const errorMessage = error.response?.data?.msg || "Error al crear la organización."
      setModalMessage(errorMessage)
      setIsModalOpen(true)
    }
  }

  const closeModal = () => setIsModalOpen(false)

  const isStepComplete = () => {
    switch (step) {
      case 1:
        return organizationName.trim() !== "" && razonSocial.trim() !== ""
      case 2:
        return direccion.trim() !== "" && ciudad.trim() !== ""
      case 3:
        return NIT.trim() !== ""
      default:
        return false
    }
  }

  if (isLoading) {
    return <Loader />
  }

  const getStepIcon = (stepNumber: number) => {
    switch (stepNumber) {
      case 1:
        return <FaBuilding className="text-lg" />
      case 2:
        return <FaMapMarkerAlt className="text-lg" />
      case 3:
        return <FaIdCard className="text-lg" />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar
        toggleSidebar={() => {}}
        toggleProfileMenu={() => setProfileMenuOpen(!profileMenuOpen)}
        toggleNotifications={() => {}}
        profileMenuOpen={profileMenuOpen}
        notificationsOpen={false}
        usuario={userData?.nombre || "Usuario"}
        rol={userData?.role || "Admin"}
        handleLogout={handleLogout}
        showToggleMenu={false}
        showNotificationsIcon={false}
      />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div
          className={`bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-500 transform ${animate ? "scale-102 opacity-95" : "scale-100 opacity-100"}`}
        >
          {/* Header with decorative elements */}
          <div className="relative bg-primary p-6 sm:p-10 text-white overflow-hidden">
            {/* Decorative circles */}
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-white/10 rounded-full"></div>
            <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-white/10 rounded-full"></div>

            <div className="relative z-10">
              {/* Mobile-friendly header layout */}
              <div className="flex flex-col sm:flex-row sm:items-center mb-4">
                <img src="/logos/logol.png" alt="Logo" className="sm:h-16 mb-4 sm:mb-0 sm:mr-4" />
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white">¡Bienvenido a ColStock!</h2>
                  <p className="mt-2 text-sm sm:text-base text-white/90 max-w-2xl">
                    Para comenzar a gestionar tu inventario y facturación de manera eficiente, completa los datos
                    básicos de tu empresa.
                  </p>
                </div>
              </div>

              {/* Progress Steps - Responsive for mobile */}
              <div className="mt-6 sm:mt-8">
                {/* Mobile progress indicator (simplified) */}
                <div className="sm:hidden flex justify-between items-center">
                  {[1, 2, 3].map((stepNumber) => (
                    <div key={stepNumber} className="flex flex-col items-center">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                          step >= stepNumber
                            ? "bg-white border-white text-primary"
                            : "border-white/50 text-white/70 bg-transparent"
                        }`}
                      >
                        {step > stepNumber ? <FaCheckCircle className="text-sm" /> : getStepIcon(stepNumber)}
                      </div>
                      <span className="text-xs mt-1 text-white/80">
                        {stepNumber === 1 ? "Empresa" : stepNumber === 2 ? "Ubicación" : "Fiscal"}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Desktop progress indicator (full) */}
                <div className="hidden sm:flex items-center">
                  {[1, 2, 3].map((stepNumber) => (
                    <div key={stepNumber} className="flex-1 flex items-center">
                      <div
                        className={`flex items-center justify-center w-14 h-14 rounded-full border-2 transition-all duration-300 ${
                          step >= stepNumber
                            ? "bg-white border-white text-primary"
                            : "border-white/50 text-white/70 bg-transparent"
                        }`}
                      >
                        {step > stepNumber ? <FaCheckCircle className="text-xl" /> : getStepIcon(stepNumber)}
                      </div>

                      {stepNumber < 3 && (
                        <div className="flex-1 mx-2 relative h-2 bg-white/30 rounded-full overflow-hidden">
                          <div
                            className={`absolute top-0 left-0 h-full bg-white transition-all duration-500 ${
                              step > stepNumber ? "w-full" : "w-0"
                            }`}
                          ></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 sm:p-10">
            <form onSubmit={step === 3 ? handleCreateOrganization : (e) => e.preventDefault()} className="space-y-6">
              {step === 1 && (
                <div
                  className={`transition-all duration-500 transform ${animate ? "translate-x-4 opacity-0" : "translate-x-0 opacity-100"}`}
                >
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre de Empresa
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaBuilding className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            id="organizationName"
                            type="text"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            value={organizationName}
                            onChange={(e) => setOrganizationName(e.target.value)}
                            required
                            placeholder="Ej: Mi Empresa S.A.S"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">Introduce el nombre de tu empresa</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="razonSocial" className="block text-sm font-medium text-gray-700 mb-1">
                          Razón Social
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaIdCard className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            id="razonSocial"
                            type="text"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            value={razonSocial}
                            onChange={(e) => setRazonSocial(e.target.value)}
                            required
                            placeholder="Ej: Comercializadora Mi Empresa S.A.S"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">Introduce la razón social de tu empresa</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={!isStepComplete()}
                      className={`flex items-center justify-center py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                        isStepComplete()
                          ? "bg-primary hover:bg-primary/90 hover:shadow-lg transform hover:-translate-y-0.5"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Siguiente <FaArrowRight className="ml-2" />
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div
                  className={`transition-all duration-500 transform ${animate ? "translate-x-4 opacity-0" : "translate-x-0 opacity-100"}`}
                >
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="direccion" className="block text-sm font-medium text-gray-700 mb-1">
                          Dirección
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaMapMarkerAlt className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            id="direccion"
                            type="text"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            value={direccion}
                            onChange={(e) => setDireccion(e.target.value)}
                            required
                            placeholder="Ej: Calle 123 # 45-67"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">Introduce la dirección de tu empresa</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="ciudad" className="block text-sm font-medium text-gray-700 mb-1">
                          Ciudad
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaMapMarkerAlt className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            id="ciudad"
                            type="text"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            value={ciudad}
                            onChange={(e) => setCiudad(e.target.value)}
                            required
                            placeholder="Ej: Bogotá"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">Introduce la ciudad de tu empresa</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                          <label htmlFor="telefonoContacto" className="block text-sm font-medium text-gray-700 mb-1">
                            Contacto
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaPhone className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              id="telefonoContacto"
                              type="text"
                              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                              value={telefonoContacto}
                              onChange={(e) => setTelefonoContacto(e.target.value)}
                              required
                              placeholder="Ej: 3123456789"
                            />
                          </div>
                        <p className="mt-1 text-sm text-gray-500">Introduce el contacto de tu empresa</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex items-center justify-center py-3 px-6 rounded-lg font-medium text-primary border border-primary hover:bg-primary/5 transition-all duration-200"
                    >
                      <FaArrowLeft className="mr-2" /> Atrás
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={!isStepComplete()}
                      className={`flex items-center justify-center py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                        isStepComplete()
                          ? "bg-primary hover:bg-primary/90 hover:shadow-lg transform hover:-translate-y-0.5"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Siguiente <FaArrowRight className="ml-2" />
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div
                  className={`transition-all duration-500 transform ${animate ? "translate-x-4 opacity-0" : "translate-x-0 opacity-100"}`}
                >
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="NIT" className="block text-sm font-medium text-gray-700 mb-1">
                          NIT
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaIdCard className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            id="NIT"
                            type="text"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            value={NIT}
                            onChange={(e) => setNIT(e.target.value)}
                            required
                            placeholder="Ej: 900.123.456-7"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">Introduce el NIT de tu empresa</p>
                      </div>
                    </div>

                    <div className="space-y-4 flex items-center">
                      <div className="bg-gray-50 p-4 sm:p-6 rounded-xl border border-gray-200 w-full">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="responsableIVA"
                            checked={responsableIVA}
                            onChange={(e) => setResponsableIVA(e.target.checked)}
                            className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                          />
                          <label htmlFor="responsableIVA" className="ml-2 block text-base font-medium text-gray-700">
                            Responsable de IVA
                          </label>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Marca esta opción si tu empresa es responsable de IVA según la normativa fiscal.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex items-center justify-center py-3 px-6 rounded-lg font-medium text-primary border border-primary hover:bg-primary/5 transition-all duration-200"
                    >
                      <FaArrowLeft className="mr-2" /> Atrás
                    </button>
                    <button
                      type="submit"
                      disabled={!isStepComplete()}
                      className={`flex items-center justify-center py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                        isStepComplete()
                          ? "bg-primary hover:bg-primary/90 hover:shadow-lg transform hover:-translate-y-0.5"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Crear Organización <FaCheck className="ml-2" />
                    </button>
                  </div>
                </div>
              )}
            </form>

            {notification && (
              <div className="mt-6 p-4 bg-primary/10 text-primary rounded-lg flex items-center animate-fadeIn">
                <FaCheck className="mr-2" /> {notification}
              </div>
            )}
          </div>
        </div>
      </div>

      <MessageModal isOpen={isModalOpen} message={modalMessage} messageType="error" onClose={closeModal} />
    </div>
  )
}

export default CreateOrganization

